import styled from 'styled-components';

import { GroupColors } from '../groupColors';

/**
 * Styled tokens for the Overview summary widgets (`parts/overview/*`).
 *
 * Every color is sourced from the shared `GroupColors` theme tokens so the
 * Overview subtree carries no hardcoded hex literal and resolves under both the
 * DEFAULT and HACKERU themes (Requirement 11). The widget shell is a native
 * `<button>` so each summary card is keyboard-activatable and announced as an
 * interactive control — clicking or pressing Enter/Space navigates to the
 * widget's dedicated tab (Requirement 2.4).
 */

/** Responsive three-up grid that composes the summary widgets. */
export const SummaryGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Clickable widget shell. Rendered as a `<button>` so it is reachable by Tab,
 * activatable by Enter/Space, and exposes a focus ring — no extra `role`/key
 * handlers required (Requirement 2.4, keyboard activation).
 */
export const WidgetCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
  width: 100%;
  font: inherit;
  background: ${GroupColors.surface};
  border: 1px solid ${GroupColors.border};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  color: ${GroupColors.textPrimary};
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    border-color: ${GroupColors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${GroupColors.accent};
    outline-offset: 2px;
  }
`;

export const WidgetTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${GroupColors.textSecondary};
`;

/** Centered placeholder used by both the loading and empty states. */
export const WidgetPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  color: ${GroupColors.textMuted};
  font-size: 12px;
  font-weight: 500;
`;

/** Compact loader so the widget shell keeps its height while refreshing. */
export const WidgetLoader = styled(WidgetPlaceholder)`
  & > div {
    width: 28px;
    height: 28px;
  }
`;

/** A single metric line inside a populated widget. */
export const Metric = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

export const MetricLabel = styled.span`
  font-size: 12px;
  color: ${GroupColors.textSecondary};
`;

export const MetricValue = styled.span<{
  tone?: 'success' | 'warn' | 'danger';
}>`
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ tone }) => {
    if (tone === 'success') return GroupColors.success;
    if (tone === 'warn') return GroupColors.warn;
    if (tone === 'danger') return GroupColors.danger;
    return GroupColors.textPrimary;
  }};
`;

/** Per-currency row inside the money widget. */
export const CurrencyRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
  border-bottom: 1px solid ${GroupColors.border};

  &:last-child {
    border-bottom: 0;
  }
`;

export const CurrencyCode = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${GroupColors.textMuted};
`;
